// JSON-LD Schema generators for SEO

// Generate JSON-LD for listical pages (homepage, country, type, variety, region)
export const generateListPageJsonLd = (pageTitle, pageDescription, deals, pageUrl) => {
  if (!deals || deals.length === 0) return null;
  
  const prices = deals.map(d => d.price).filter(p => p > 0);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": pageTitle,
    "description": pageDescription,
    "url": pageUrl,
    "numberOfItems": deals.length,
    "itemListElement": deals.slice(0, 10).map((deal, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": deal.name,
        "url": `${typeof window !== 'undefined' ? window.location.origin : 'https://greatwine.deals'}${deal.detail_url || '/' + deal.id}`,
        "image": deal.image_url,
        "description": deal.description || deal.tasting_notes || `${deal.wine_type} from ${deal.country}`,
        "offers": {
          "@type": "Offer",
          "price": deal.price.toFixed(2),
          "priceCurrency": "USD"
        },
        ...(deal.rating && {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": deal.rating.match(/(\d+)/)?.[1] || deal.rating,
            "bestRating": "100",
            "worstRating": "0",
            "ratingCount": 1,
            "reviewCount": 1
          }
        })
      }
    }))
  };

  // Add aggregate offer for price range
  if (prices.length > 1) {
    jsonLd.offers = {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": minPrice.toFixed(2),
      "highPrice": maxPrice.toFixed(2),
      "offerCount": deals.length
    };
  }

  return jsonLd;
};

// Generate JSON-LD for individual wine product page
export const generateProductJsonLd = (deal) => {
  if (!deal) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": deal.name,
    "url": `${typeof window !== 'undefined' ? window.location.origin : 'https://greatwine.deals'}${deal.detail_url || '/' + deal.id}`,
    "image": deal.image_url,
    "description": deal.description || deal.tasting_notes || `${deal.wine_type} from ${deal.country}`,
    "category": deal.wine_type,
    "countryOfOrigin": {
      "@type": "Country",
      "name": deal.country
    },
    "offers": {
      "@type": "Offer",
      "price": deal.price.toFixed(2),
      "priceCurrency": "USD"
    }
  };

  // Add rating/review if available
  if (deal.rating) {
    const ratingValue = deal.rating.match(/(\d+)/)?.[1] || deal.rating;
    const ratingSource = deal.rating.replace(/\d+/g, '').trim() || 'Wine Expert';
    
    jsonLd.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": ratingValue,
      "bestRating": "100",
      "worstRating": "0",
      "ratingCount": 1,
      "reviewCount": 1
    };

    jsonLd.review = {
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": ratingValue,
        "bestRating": "100",
        "worstRating": "0"
      },
      "author": {
        "@type": "Organization",
        "name": ratingSource || "Wine Expert"
      },
      "reviewBody": deal.tasting_notes || deal.description || `Rated ${deal.rating} - excellent value wine.`
    };
  }

  // Add additional product properties
  if (deal.variety) {
    jsonLd.additionalProperty = [
      {
        "@type": "PropertyValue",
        "name": "Grape Variety",
        "value": deal.variety
      }
    ];
  }

  if (deal.region) {
    jsonLd.additionalProperty = jsonLd.additionalProperty || [];
    jsonLd.additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Region",
      "value": deal.region
    });
  }

  if (deal.appellation) {
    jsonLd.additionalProperty = jsonLd.additionalProperty || [];
    jsonLd.additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Appellation",
      "value": deal.appellation
    });
  }

  return jsonLd;
};
